import { Component } from '@theme/component';
import { isQuarterHourTimeValue } from '@theme/utilities';

const ALLOWED_MINUTES = new Set([0, 15, 30, 45]);

/**
 * Custom time field: keyboard accepts 00 / 15 / 30 / 45, dropdown lists only those minutes.
 * @typedef {object} QuarterHourTimeRefs
 * @property {HTMLInputElement} [input]
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} panel
 * @property {HTMLElement} notice
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 *
 * @extends {Component<QuarterHourTimeRefs>}
 */
class QuarterHourTime extends Component {
  requiredRefs = ['toggle', 'panel', 'notice', 'hours', 'minutes'];

  /**
   * @returns {HTMLInputElement | null}
   */
  get #input() {
    const fromRef = this.refs.input;
    if (fromRef instanceof HTMLInputElement) return fromRef;
    const nested = this.querySelector('.quarter-hour-time__input');
    return nested instanceof HTMLInputElement ? nested : null;
  }

  /** @type {string} */
  #lastValidValue = '';

  #disabledObserver = new MutationObserver(() => this.#syncDisabled());

  connectedCallback() {
    super.connectedCallback();
    const input = this.#input;
    this.#lastValidValue = isQuarterHourTimeValue(input?.value) ? input.value : '';
    this.#syncDisabled();
    this.#syncSelectedOptions();
    if (input) {
      input.addEventListener('invalid', this.handleInvalid);
      this.#disabledObserver.observe(input, { attributes: true, attributeFilter: ['disabled'] });
    }
    document.addEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.addEventListener('keydown', this.#handleDocumentKeydown);
  }

  disconnectedCallback() {
    this.#input?.removeEventListener('invalid', this.handleInvalid);
    this.#disabledObserver.disconnect();
    document.removeEventListener('pointerdown', this.#handleDocumentPointerDown);
    document.removeEventListener('keydown', this.#handleDocumentKeydown);
    super.disconnectedCallback();
  }

  handleToggleClick = (event) => {
    event.preventDefault();
    if (this.#input?.disabled) return;

    if (this.#isOpen()) {
      this.closePanel();
      return;
    }

    this.openPanel();
  };

  handleInput = () => {
    const input = this.#input;
    if (!input) return;
    const formatted = formatTimeDraft(input.value);
    if (formatted !== input.value) input.value = formatted;

    this.#syncSelectedOptions();

    if (!input.value) {
      this.#hideNotice();
      input.setCustomValidity('');
      this.#lastValidValue = '';
    } else if (!isCompleteTime(input.value)) {
      this.#hideNotice();
      input.setCustomValidity('');
    } else if (isQuarterHourTimeValue(input.value)) {
      const normalized = normalizeTimeValue(input.value);
      input.value = normalized;
      this.#lastValidValue = normalized;
      this.#hideNotice();
      input.setCustomValidity('');
      this.#syncSelectedOptions();
    } else {
      this.#showNotice();
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  handleBlur = () => {
    const input = this.#input;
    if (!input) return;
    if (!input.value) {
      this.#hideNotice();
      input.setCustomValidity('');
      return;
    }

    if (isQuarterHourTimeValue(input.value)) {
      const normalized = normalizeTimeValue(input.value);
      input.value = normalized;
      this.#lastValidValue = normalized;
      this.#hideNotice();
      input.setCustomValidity('');
      this.#syncSelectedOptions();
      return;
    }

    this.#showNotice();
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * @param {Event} event
   */
  handleHourClick = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const hour = button.dataset.hour;
    if (hour == null) return;
    this.#applyPart({ hour });
  };

  /**
   * @param {Event} event
   */
  handleMinuteClick = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const minute = button.dataset.minute;
    if (minute == null) return;
    this.#applyPart({ minute });
  };

  handleInvalid = (event) => {
    const input = this.#input;
    if (!(event.target instanceof HTMLInputElement) || event.target !== input) return;
    if (!input.value || isQuarterHourTimeValue(input.value)) return;
    event.preventDefault();
    this.#showNotice();
  };

  openPanel() {
    this.refs.panel.hidden = false;
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.#syncSelectedOptions();
    this.#scrollSelectedHourIntoView();
  }

  closePanel() {
    this.refs.panel.hidden = true;
    this.refs.toggle.setAttribute('aria-expanded', 'false');
  }

  showNotice() {
    this.#showNotice();
  }

  /**
   * @param {{ hour?: string, minute?: string }} parts
   */
  #applyPart(parts) {
    const input = this.#input;
    if (!input) return;
    const current = parseTimeParts(input.value) || parseTimeParts(this.#lastValidValue) || { hour: 12, minute: 0 };
    const hour = parts.hour != null ? Number(parts.hour) : current.hour;
    const minute = parts.minute != null ? Number(parts.minute) : current.minute;
    const next = `${padTime(hour)}:${padTime(minute)}`;

    input.value = next;
    this.#lastValidValue = next;
    this.#hideNotice();
    input.setCustomValidity('');
    this.#syncSelectedOptions();
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (parts.minute != null) this.closePanel();
  }

  #isOpen() {
    return !this.refs.panel.hidden;
  }

  #showNotice() {
    const input = this.#input;
    const message = this.dataset.invalidMessage || 'Please select a time from the dropdown.';
    if (input) input.setCustomValidity(message);
    this.refs.notice.hidden = false;
  }

  #hideNotice() {
    this.refs.notice.hidden = true;
  }

  #syncDisabled() {
    const disabled = Boolean(this.#input?.disabled);
    this.refs.toggle.disabled = disabled;
    if (disabled) {
      this.closePanel();
      this.#hideNotice();
    }
  }

  #syncSelectedOptions() {
    const parsed = parseTimeParts(this.#input?.value || '');
    const hour = parsed?.hour;
    const minute = parsed?.minute;

    for (const option of this.refs.hours.querySelectorAll('[data-hour]')) {
      if (!(option instanceof HTMLElement)) continue;
      option.setAttribute('aria-selected', String(Number(option.dataset.hour) === hour));
    }

    for (const option of this.refs.minutes.querySelectorAll('[data-minute]')) {
      if (!(option instanceof HTMLElement)) continue;
      option.setAttribute('aria-selected', String(Number(option.dataset.minute) === minute));
    }
  }

  #scrollSelectedHourIntoView() {
    const selected = this.refs.hours.querySelector('[aria-selected="true"]');
    if (selected instanceof HTMLElement) {
      selected.scrollIntoView({ block: 'nearest' });
    }
  }

  #handleDocumentPointerDown = (event) => {
    if (!(event.target instanceof Node)) return;
    if (this.contains(event.target)) return;
    this.closePanel();
  };

  #handleDocumentKeydown = (event) => {
    if (event.key === 'Escape') this.closePanel();
  };
}

/**
 * @param {string} value
 * @returns {string}
 */
function formatTimeDraft(value) {
  const digits = String(value).replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isCompleteTime(value) {
  return /^\d{1,2}:\d{2}$/.test(value.trim());
}

/**
 * @param {string} value
 * @returns {{ hour: number, minute: number } | null}
 */
function parseTimeParts(value) {
  const match = String(value || '')
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;

  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || hour < 0 || hour > 23) return null;
  if (!Number.isFinite(minute) || minute < 0 || minute > 59) return null;
  return { hour, minute };
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeTimeValue(value) {
  const parsed = parseTimeParts(value);
  if (!parsed || !ALLOWED_MINUTES.has(parsed.minute)) return '';
  return `${padTime(parsed.hour)}:${padTime(parsed.minute)}`;
}

/**
 * @param {number} value
 * @returns {string}
 */
function padTime(value) {
  return String(value).padStart(2, '0');
}

if (!customElements.get('quarter-hour-time')) {
  customElements.define('quarter-hour-time', QuarterHourTime);
}
